/**
 * Two tabs of a Kafka topic: Partitions (per partition the first and next offset kept, the messages, the leader and
 * replicas; the topic's retention) and Produce (a key, a value and headers sent as one message).
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import DataTable from '../table/DataTable';
import { ErrorState, Loading, errorText } from './DataCommon';
import { formatBytes, formatCount, parseLooseJson } from '../../utils/dataModel';

function duration(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) { return 'forever'; }
  if (n % 86400000 === 0) { return `${n / 86400000} day${n === 86400000 ? '' : 's'}`; }
  if (n % 3600000 === 0) { return `${n / 3600000} h`; }
  return `${Math.round(n / 60000)} min`;
}

export function retentionText(config = {}) {
  const parts = [];
  if (config['cleanup.policy']) { parts.push(config['cleanup.policy'] === 'compact' ? 'compacted (the latest per key is kept)' : `cleanup ${config['cleanup.policy']}`); }
  if (config['retention.ms'] !== undefined) { parts.push(`kept ${duration(config['retention.ms'])}`); }
  if (config['retention.bytes'] !== undefined && Number(config['retention.bytes']) > 0) { parts.push(`at most ${formatBytes(Number(config['retention.bytes']))} per partition`); }
  return parts.join(' · ');
}

const PARTITION_COLUMNS = [
  { key: 'partition', label: 'Partition', className: 'data-mono', value: (p) => p.partition },
  { key: 'low', label: 'First offset', headerTitle: 'Low watermark: the oldest offset still in the log', className: 'data-mono', value: (p) => p.low, render: (p) => formatCount(p.low) },
  { key: 'high', label: 'End offset', headerTitle: 'High watermark: the offset the next message gets', className: 'data-mono', value: (p) => p.high, render: (p) => formatCount(p.high) },
  { key: 'messages', label: 'Messages', className: 'data-mono', value: (p) => p.messages, render: (p) => formatCount(p.messages) },
  { key: 'leader', label: 'Leader', className: 'data-mono', value: (p) => p.leader ?? '—' },
  { key: 'replicas', label: 'Replicas', className: 'data-mono', value: (p) => (p.replicas || []).join(', ') },
  { key: 'isr', label: 'In sync', className: 'data-mono', value: (p) => (p.isr || []).join(', ') },
];

export function PartitionsTab({ partitions, error, onRetry }) {
  if (error) { return <ErrorState error={error} onRetry={onRetry} />; }
  if (!partitions) { return <Loading label="Loading the partitions…" />; }
  const retention = retentionText(partitions.config);
  return (
    <section aria-labelledby="data-partitions-title" className="data-card">
      <div className="data-card-head">
        <h2 id="data-partitions-title">Partitions</h2>
        {retention && <span className="data-muted">{retention}</span>}
      </div>
      <div className="data-table-wrap">
        <DataTable
          className="data-table"
          columns={PARTITION_COLUMNS}
          rowKey="partition"
          rowProps={() => ({ 'data-testid': 'kafka-partition' })}
          rows={partitions.partitions}
          storageKey="flow.data.kafkaPartitions.hiddenColumns"
        />
      </div>
      {partitions.partitions.some((p) => p.low > 0) && (
        <p className="data-footnote is-padded">A first offset above 0: Kafka removed the older messages (retention or compaction). What a group committed stays.</p>
      )}
    </section>
  );
}

PartitionsTab.propTypes = { error: PropTypes.string, onRetry: PropTypes.func.isRequired, partitions: PropTypes.object };

export function ProduceForm({ onSend }) {
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [headers, setHeaders] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null);
  const submit = async (event) => {
    event.preventDefault();
    const parsed = parseLooseJson(headers);
    if (!parsed.ok || (parsed.value !== undefined && (typeof parsed.value !== 'object' || Array.isArray(parsed.value)))) {
      setError('Headers are a JSON object, e.g. { "source": "flow" }');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const out = await onSend({ key: key || undefined, value, headers: parsed.value });
      setValue('');
      setSent(out && out.offset !== undefined && out.offset !== null ? `Sent to partition ${out.partition} at offset ${out.offset}` : 'Sent');
      setTimeout(() => setSent(null), 2500);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form aria-labelledby="data-produce-title" className="data-card data-produce" onSubmit={submit}>
      <h2 id="data-produce-title">Produce message</h2>
      <div className="data-form-grid">
        <label className="data-label" htmlFor="data-produce-key">Key</label>
        <input className="data-input data-mono" id="data-produce-key" onChange={(e) => setKey(e.target.value)} placeholder="optional" value={key} />
        <label className="data-label" htmlFor="data-produce-value">Value</label>
        <textarea className="data-textarea data-mono" id="data-produce-value" onChange={(e) => setValue(e.target.value)} placeholder='{ "orderId": 42 }' rows={4} spellCheck={false} value={value} />
        <label className="data-label" htmlFor="data-produce-headers">Headers</label>
        <input className="data-input data-mono" id="data-produce-headers" onChange={(e) => setHeaders(e.target.value)} placeholder='optional, e.g. { "source": "flow" }' value={headers} />
      </div>
      {error && <p className="data-error-text" role="alert">{error}</p>}
      <div className="data-actions">
        <button className="data-btn is-primary" disabled={busy || value === ''} type="submit">{busy ? 'Sending…' : 'Send'}</button>
        {sent && <span className="data-pill is-ok" role="status">{sent}</span>}
      </div>
    </form>
  );
}

ProduceForm.propTypes = { onSend: PropTypes.func.isRequired };
